import { useEffect, useState } from 'react';
import {
  NavigateFunction,
  generatePath,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  FOOTBALL_PLAY_RIVALS,
  FOOTBALL_PLAY_RIVALS_DRAFT_SLUG,
  FOOTBALL_PLAY_RIVALS_MATCHES,
  FOOTBALL_PLAY_RIVALS_MATCHES_DATE_SLUG_FRIENDLIES,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useIsAndroidPhone } from '@sorare/core/src/hooks/device/useIsAndroidPhone';
import { useIsIPhone } from '@sorare/core/src/hooks/device/useIsIPhone';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { formatDateForUrl, useTypedParams } from '@sorare/core/src/lib/routing';

import { isGameScheduled } from '@sorare/gameplay/src/lib/so5';

import { RivalsInviteSignupDialog } from 'components/rivals/RivalsInviteSignupDialog';

import { PageParams } from './__generated__/routeParams';
import { FootballRivalsInviteAcceptCodeMutation } from './__generated__/useFootballRivalsInviteAcceptCode.graphql';
import useFootballRivalsInviteAcceptCode from './useFootballRivalsInviteAcceptCode';

const navigateAfterCodeAccepted = (
  navigate: NavigateFunction,
  showNotification: ReturnType<
    typeof useSnackNotificationContext
  >['showNotification'],
  rivalsGame:
    | NonNullable<
        FootballRivalsInviteAcceptCodeMutation['footballRivalsInviteAcceptCode']
      >['game']
    | undefined
    | null
) => {
  if (rivalsGame) {
    const tooLateToComposeLineup = !isGameScheduled(rivalsGame.game?.status);
    const alreadyHasLineup = !!rivalsGame.myLineup?.id;

    if (tooLateToComposeLineup && !alreadyHasLineup) {
      showNotification('rivalsInviteMatchAlreadyStarted');
      navigate(FOOTBALL_PLAY_RIVALS_MATCHES);
    } else if (alreadyHasLineup) {
      navigate(
        generatePath(FOOTBALL_PLAY_RIVALS_MATCHES_DATE_SLUG_FRIENDLIES, {
          date: formatDateForUrl(rivalsGame.game.date),
          slug: rivalsGame.slug,
        })
      );
    } else {
      navigate(
        generatePath(FOOTBALL_PLAY_RIVALS_DRAFT_SLUG, {
          slug: rivalsGame.slug,
        })
      );
    }
  } else {
    navigate(generatePath(FOOTBALL_PLAY_RIVALS));
  }
};

const RivalsInvite = () => {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUserContext();
  const [accept, { loading }] = useFootballRivalsInviteAcceptCode();
  const { code } = useTypedParams<PageParams>();
  const [hasTriedToAcceptCode, setHasTriedToAcceptCode] = useState(false); // to avoid re-triggering the mutation on re-renders
  const { showNotification } = useSnackNotificationContext();
  const {
    flags: { useOnelinkRedirection = false },
  } = useFeatureFlags();
  const isIphone = useIsIPhone();
  const isAndroidPhone = useIsAndroidPhone();
  const location = useLocation();
  const { isAndroidApp } = useIsMobileApp();

  const shouldRedirectToAppStore =
    (isIphone || (isAndroidPhone && !isAndroidApp)) && useOnelinkRedirection;

  useEffect(() => {
    // Disable default afterLoggedInTarget by explicitely telling to redirect to the same url after signin/signup
    navigate(`.${location.search}`, {
      state: { afterLoggedInTarget: location.pathname },
    });
  }, [location.search, location.pathname, navigate]);

  if (!currentUser || shouldRedirectToAppStore) {
    return (
      <RivalsInviteSignupDialog
        code={code}
        shouldRedirectToAppStore={shouldRedirectToAppStore}
      />
    );
  }

  if (!loading && !hasTriedToAcceptCode) {
    setHasTriedToAcceptCode(true);
    accept({ code }).then(({ data }) => {
      const { game } = data?.footballRivalsInviteAcceptCode || {};
      navigateAfterCodeAccepted(navigate, showNotification, game);
    });
  }

  return <LoadingIndicator fullScreen />;
};

export default RivalsInvite;
